import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLogin from '../components/admin/AdminLogin';
import AdminNav from '../components/admin/AdminNav';
import { getSesion } from '../utils/sesionAdmin';
import { llevarSetterASetting, setterAlEntrar } from '../utils/setterAdmin';
import { ordenarClientes, COLUMNAS_ORDENABLES } from '../utils/ordenarClientes';
import {
  fmtMonto, fmtPct, fmtFecha, nombreQuincena, nombreMes, mensajeConfirmacionPago,
} from '../utils/formatoFinanzas';
import '../styles/admin.css';
import '../styles/admin-finanzas.css';
import { ADMIN_API } from '../utils/adminApi';


// ---------- Helpers de formato ----------

const minutosDesde = (iso) => {
  if (!iso) return null;
  const diffMs = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.round(diffMs / 60000));
};

// ---------- Subcomponentes ----------

const ResumenSection = ({ resumen, corte }) => {
  const r = resumen || {};
  return (
    <section className="au-fin-section">
      <h2 className="au-fin-section-title">Resumen</h2>
      <div className="au-fin-cards-grid">
        <div className="au-fin-card au-fin-stat">
          <div className="au-fin-card-label">Clientes activos</div>
          <div className="au-fin-card-value">{r.clientesActivos ?? 0}</div>
        </div>
        <div className="au-fin-card au-fin-stat">
          <div className="au-fin-card-label">Cobrado del mes</div>
          <div className="au-fin-card-value">{fmtMonto(r.cobradoMes)}</div>
        </div>
        <div className="au-fin-card au-fin-stat">
          <div className="au-fin-card-label">Deuda total</div>
          <div className="au-fin-card-value hot">{fmtMonto(r.deudaTotal)}</div>
        </div>
        <div className="au-fin-card au-fin-stat">
          <div className="au-fin-card-label">Próximo corte</div>
          <div className="au-fin-card-value">{fmtFecha(corte)}</div>
        </div>
      </div>
    </section>
  );
};

// Recordatorio del F29, solo para Agustín. Cada período sin marcar como pagado
// queda destacado desde el día 1 del mes siguiente, que es cuando se puede
// pagar, hasta que lo marque. Lo que el panel sabe son las retenciones de
// honorarios de las boletas de los coaches; el F29 puede llevar más cosas.
const F29Section = ({ f29, paying, error, onMarcar }) => {
  if (!Array.isArray(f29) || f29.length === 0) return null;
  const pendientes = f29.filter((p) => !p.pagado);
  const ultimoPagado = [...f29].reverse().find((p) => p.pagado);
  return (
    <section className="au-fin-section">
      <h2 className="au-fin-section-title">F29</h2>
      {pendientes.length === 0 && ultimoPagado && (
        <div className="au-fin-card">
          <span className="au-fin-status ok">Al día</span>
          <span className="muted mono">
            {' '}· F29 de {nombreMes(ultimoPagado.periodo)} pagado el {ultimoPagado.pagado.pagadoEl}
          </span>
        </div>
      )}
      {pendientes.map((p) => {
        const vencido = p.diasParaPlazo < 0;
        return (
          <div className="au-fin-card au-fin-f29" key={p.periodo}>
            <div className="au-fin-coach-card-head">
              <div className="name">F29 de {nombreMes(p.periodo)}</div>
              <span className={`au-fin-badge ${vencido ? 'hot' : 'pending'}`}>
                {vencido
                  ? `Venció el ${fmtFecha(p.plazo)} · hace ${-p.diasParaPlazo} día(s)`
                  : `Vence el ${fmtFecha(p.plazo)} · quedan ${p.diasParaPlazo} día(s)`}
              </span>
            </div>
            <div className="muted mono au-fin-proy-nota">
              Se puede pagar desde el {fmtFecha(p.desde)} en sii.cl.
            </div>
            <div className="au-fin-liq-breakdown">
              <div className="row head">
                <span>Retenciones de honorarios ({p.boletas.length} boleta(s))</span>
                <span>{fmtMonto(p.retenciones)}</span>
              </div>
              {p.boletas.map((b) => (
                <div className="row sub" key={`${b.coachNombre}-${b.corte}`}>
                  <span>· {b.coachNombre} · corte {fmtFecha(b.corte)} · bruto {fmtMonto(b.bruto)}</span>
                  <span>{fmtMonto(b.retencionMonto)}</span>
                </div>
              ))}
            </div>
            <div className="muted mono au-fin-proy-nota">
              Sale de los pagos a coaches registrados en este panel. Si el F29 lleva algo más (IVA, PPM), no está acá.
            </div>
            {error && <div className="au-fin-error">&lt; {error}</div>}
            <button className="au-fin-pay-btn" disabled={paying} onClick={() => onMarcar(p)}>
              {paying ? 'REGISTRANDO…' : 'MARCAR F29 PAGADO'}
            </button>
          </div>
        );
      })}
    </section>
  );
};

// Si a la quincena le falta plata por caer, y de qué tipo. Un moroso es un
// problema (hay que ir a cobrarle); un cobro que todavía no le toca al cliente
// no lo es, y mezclarlos haría que ninguna quincena en curso se viera nunca
// bien. Por eso son dos avisos distintos y solo el moroso pinta en rojo.
const EstadoQuincena = ({ corte }) => {
  const nMorosos = (corte.morosos || []).length;
  const nPorVenir = (corte.porVenir || []).length;

  if (nMorosos > 0) {
    return (
      <span className="au-fin-badge hot">
        ⚠ Falta{nMorosos === 1 ? '' : 'n'} {nMorosos} por pagar
      </span>
    );
  }
  if (nPorVenir > 0) {
    return (
      <span className="au-fin-badge encurso">
        {nPorVenir} por caer
      </span>
    );
  }
  return <span className="au-fin-badge ok">✓ Completo</span>;
};

// Un bloque por corte quincenal: tabla de cobros (fecha de cobro de Flow +
// fecha en que entró la plata), desglose y botón de pago si ya es pagable.
//
// La comisión va como texto plano, igual para todas las tasas: Agustín no
// quiere que el 40% y el 60% se distingan a la vista (2026-10-21). Antes iban
// en recuadros de color, y eso marcaba justo la diferencia.
const Comision = ({ tasa }) => <span className="au-fin-tasa">{fmtPct(tasa)}</span>;

// Mes, comisión, monto, bruto y líquido de una fila de la quincena. "Mes" es
// cuántos meses lleva el cliente en AU con ese cobro (la antigüedad que se
// quiere hacer crecer).
// El líquido por fila es informativo: el del corte sale de una sola retención
// sobre el bruto total, como la boleta, y puede diferir en un peso de la suma.
// El verbo es lo que evita la duda de si el mes ya se pagó o falta pagarlo.
const VERBO_MES = { pagado: 'Pagó', moroso: 'Debe', porVenir: 'Paga' };

const CeldasReparto = ({ fila, tipo, base, retencion }) => {
  const tasa = typeof fila.comision === 'number' ? fila.comision : base;
  const bruto = Math.round(Number(fila.monto || 0) * tasa);
  const liquido = bruto - Math.round(bruto * retencion);
  const mes = fila.mes ?? fila.nCobro;
  return (
    <>
      <td>
        {fila.esAjuste || !mes
          ? <span className="muted mono">—</span>
          : <span className="au-fin-tasa">{VERBO_MES[tipo]} mes {mes}</span>}
      </td>
      <td><Comision tasa={tasa} /></td>
      <td>{fmtMonto(fila.monto)}</td>
      <td>{fmtMonto(bruto)}</td>
      <td>{fmtMonto(liquido)}</td>
    </>
  );
};

// El coach ve el mismo bloque con dos cosas menos: la fila "Tu parte" (el
// margen de AU no es asunto suyo) y el botón de pago (nadie se registra su
// propio pago; la ruta además exige rol admin, esto es solo la UI).
const CorteBlock = ({ coach, corte, paying, error, onMarcarPagado, esAdmin }) => {
  const retPct = corte.bruto > 0 ? corte.retencionMonto / corte.bruto : 0;
  const pagable = corte.estado === 'pagable';
  // Tasa de lo recolectado: null cuando la quincena mezcla clientes antiguos
  // con clientes nuevos del reparto escalonado. Un payload sin el campo (backend
  // anterior) cae a la del coach, que es lo que significaba antes.
  const tasa = corte.comision === undefined ? coach.comision : corte.comision;
  const retencion = corte.retencion ?? retPct;

  const morosos = corte.morosos || [];
  const porVenir = corte.porVenir || [];
  const nClientes = corte.cobros.length + morosos.length + porVenir.length;

  return (
    <div className="au-fin-corte-block">
      <div className="au-fin-coach-card-head">
        <div className="name au-fin-corte-title">{nombreQuincena(corte.corte)}</div>
        {pagable ? (
          <span className="au-fin-badge pending">
            {esAdmin ? 'Pagar' : 'Por pagar'} · corte {fmtFecha(corte.corte)}
          </span>
        ) : (
          <span className="au-fin-badge encurso">En curso · corte {fmtFecha(corte.corte)}</span>
        )}
        <EstadoQuincena corte={corte} />
      </div>
      <div className="au-fin-coach-period muted mono">{corte.periodo}</div>

      {/* Una sola tabla con TODOS los clientes de la quincena. Separar los
          pagados de los que faltan en dos tablas obligaba a sumar de cabeza
          para saber si el corte estaba completo. */}
      <div className="au-fin-table-scroll">
        <table className="au-fin-table">
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Cobro Flow</th>
              <th>Pagado</th>
              <th>Mes</th>
              <th>Comisión</th>
              <th>Monto</th>
              <th>Bruto</th>
              <th>Líquido</th>
            </tr>
          </thead>
          <tbody>
            {corte.cobros.map((c, i) => (
              <tr key={`${c.invoiceId || i}-${c.periodStart}`}>
                <td>
                  {c.cliente || c.email}
                  {/* El motivo no es decoración: un monto que no viene de un
                      cobro de Flow, en una tabla de plata y sin explicación al
                      lado, se lee como error del sistema. */}
                  {c.esAjuste && c.motivo && (
                    <div className="au-fin-ajuste-motivo">{c.motivo}</div>
                  )}
                </td>
                <td>{fmtFecha(c.periodStart)}</td>
                <td>
                  {c.esAjuste
                    ? <span className="au-fin-status ajuste">Ajuste</span>
                    : fmtFecha(c.fechaCobro)}
                </td>
                <CeldasReparto fila={c} tipo="pagado" base={coach.comision} retencion={retencion} />
              </tr>
            ))}
            {morosos.map((m, i) => (
              <tr key={`moroso-${m.email}-${i}`}>
                <td>{m.cliente || m.email}</td>
                <td>{fmtFecha(m.periodStart)}</td>
                <td><span className="au-fin-status hot">Moroso · {m.diasAtraso}d</span></td>
                <CeldasReparto fila={m} tipo="moroso" base={coach.comision} retencion={retencion} />
              </tr>
            ))}
            {porVenir.map((p, i) => (
              <tr key={`porvenir-${p.email}-${i}`}>
                <td>{p.cliente || p.email}</td>
                <td>{fmtFecha(p.fecha)}</td>
                <td><span className="muted mono">Por caer</span></td>
                <CeldasReparto fila={p} tipo="porVenir" base={coach.comision} retencion={retencion} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="au-fin-liq-breakdown">
        <div className="row head">
          <span>Proyectado de la quincena ({nClientes})</span>
          <span>{fmtMonto(corte.proyectado ?? corte.totalCobrado)}</span>
        </div>
        <div className="row sub">
          <span>· Recolectado ({corte.cobros.length})</span>
          <span>{fmtMonto(corte.totalCobrado)}</span>
        </div>
        {porVenir.length > 0 && (
          <div className="row sub">
            <span>· Por caer ({porVenir.length})</span>
            <span>{fmtMonto(corte.totalPorVenir)}</span>
          </div>
        )}
        {morosos.length > 0 && (
          <div className="row sub">
            <span>· Moroso ({morosos.length})</span>
            <span className="hot">{fmtMonto(corte.totalMoroso)}</span>
          </div>
        )}
        <div className="row">
          {/* "de lo recolectado" no es decoración: con la quincena incompleta,
              el bruto no cuadra con lo proyectado y sin esto parece un error. */}
          <span>
            {tasa === null
              ? 'Bruto (de lo recolectado, a la tasa de cada cliente)'
              : `Bruto (${fmtPct(tasa)} de lo recolectado)`}
          </span>
          <span>{fmtMonto(corte.bruto)}</span>
        </div>
        <div className="row">
          <span>Retención ({fmtPct(retPct)})</span>
          <span>{fmtMonto(corte.retencionMonto)}</span>
        </div>
        <div className="row highlight">
          <span>{esAdmin ? 'Líquido a transferir' : 'Tu líquido'}</span>
          <span>{fmtMonto(corte.liquido)}</span>
        </div>
        {esAdmin && (
          <div className="row">
            <span>{tasa === null ? 'Tu parte' : `Tu parte (${fmtPct(1 - tasa)})`}</span>
            <span>{fmtMonto(corte.totalCobrado - corte.bruto)}</span>
          </div>
        )}
      </div>

      {/* En cuánto termina la quincena si entra lo que falta. Solo cuando falta
          algo: con todo cobrado estas cifras son idénticas a las de arriba y un
          "faltante $0" hace dudar de si quedó algo pendiente.
          El faltante se saca RESTANDO los totales en vez de calcularlo aparte,
          así las tres filas siempre cuadran aunque los redondeos no ayuden. */}
      {!corte.completo && corte.brutoProyectado != null && (
        <div className="au-fin-liq-breakdown au-fin-proyeccion-corte">
          {/* El encabezado declara la unidad: arriba las cifras son lo que
              paga el cliente, acá son comisión. Sin eso, "Recolectado
              $150.000" arriba y "De lo recolectado $90.000" acá se leen como
              un descuadre en vez de como dos monedas distintas. */}
          <div className="row head">
            <span>{esAdmin ? `Comisión ${coach.nombre}` : 'Tu comisión'}</span>
            <span className="muted mono">bruto · líquido</span>
          </div>
          <div className="row sub">
            <span>· De lo recolectado</span>
            <span>{fmtMonto(corte.bruto)} · {fmtMonto(corte.liquido)}</span>
          </div>
          <div className="row sub">
            <span>· De lo que falta</span>
            <span>
              {fmtMonto(corte.brutoProyectado - corte.bruto)}
              {' · '}
              {fmtMonto(corte.liquidoProyectado - corte.liquido)}
            </span>
          </div>
          {/* "Total si entra todo" y no "Proyectado de la quincena": esa fila
              ya existe arriba con los montos que pagan los clientes. Dos filas
              con el mismo nombre y cifras distintas se leen como contradicción.
              Y el "si" importa: hay un moroso de por medio, esto no está
              asegurado. */}
          <div className="row highlight">
            <span>Total si entra todo</span>
            <span>{fmtMonto(corte.brutoProyectado)} · {fmtMonto(corte.liquidoProyectado)}</span>
          </div>
        </div>
      )}

      {esAdmin && pagable && (
        <button
          className="au-fin-pay-btn"
          disabled={paying}
          onClick={() => onMarcarPagado(coach, corte)}
        >
          {paying ? 'REGISTRANDO…' : 'MARCAR COMO PAGADO'}
        </button>
      )}
      {error && <div className="au-fin-error">&lt; {error}</div>}
    </div>
  );
};

// Proyección mensual: cuánto entra si todos los clientes activos siguen
// pagando su mensualidad completa, y cómo se reparte entre coaches y AU.
//
// La del coach es la misma cuenta acotada a su cartera: cuánto pagan sus
// clientes y cuánto le queda a él. El reparto con AU no aparece — ni el total
// del negocio, ni los clientes sin asignar, ni el margen.
const ProyeccionSection = ({ proyeccion, esAdmin }) => {
  if (!proyeccion) return null;
  const { coaches = [], sinAsignar = {} } = proyeccion;

  if (!esAdmin) {
    const mia = coaches[0];
    if (!mia) return null;
    return (
      <section className="au-fin-section">
        <h2 className="au-fin-section-title">Proyección mensual</h2>
        <div className="au-fin-card">
          <div className="muted mono au-fin-proy-nota">
            Si todos tus clientes activos siguen y pagan su mensualidad completa:
          </div>
          <div className="au-fin-liq-breakdown">
            <div className="row">
              <span>Tus clientes ({mia.nClientes})</span>
              <span>{fmtMonto(mia.totalClientes)}</span>
            </div>
            <div className="row">
              <span>Tu bruto</span>
              <span>{fmtMonto(mia.brutoCoach)}</span>
            </div>
            <div className="row highlight">
              <span>Tu líquido</span>
              <span>{fmtMonto(mia.liquidoCoach)}</span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="au-fin-section">
      <h2 className="au-fin-section-title">Proyección mensual</h2>
      <div className="au-fin-card">
        <div className="muted mono au-fin-proy-nota">
          Si todos los clientes activos siguen y pagan su mensualidad completa:
        </div>
        <div className="au-fin-liq-breakdown">
          <div className="row">
            <span>Ingreso total mensual</span>
            <span>{fmtMonto(proyeccion.totalMensual)}</span>
          </div>
          {coaches.map((c) => (
            c.esTeamCoach ? (
              <React.Fragment key={c.email}>
                <div className="row">
                  <span>Clientes {c.nombre} ({c.nClientes})</span>
                  <span>{fmtMonto(c.totalClientes)}</span>
                </div>
                <div className="row sub">
                  <span>· {c.nombre} bruto / líquido</span>
                  <span>{fmtMonto(c.brutoCoach)} / {fmtMonto(c.liquidoCoach)}</span>
                </div>
                <div className="row sub">
                  <span>· Parte AU</span>
                  <span>{fmtMonto(c.margenAu)}</span>
                </div>
              </React.Fragment>
            ) : (
              <div className="row" key={c.email}>
                <span>Clientes tuyos ({c.nClientes}) — 100% AU</span>
                <span>{fmtMonto(c.totalClientes)}</span>
              </div>
            )
          ))}
          {/* Queda a la vista pero colgando: no entra al total de AU hasta
              que el cliente tenga coach en la app. */}
          {sinAsignar.nClientes > 0 && (
            <div className="row">
              <span>Sin coach asignado ({sinAsignar.nClientes}) — no suma a AU</span>
              <span className="muted">{fmtMonto(sinAsignar.total)}</span>
            </div>
          )}
          <div className="row highlight">
            <span>Total AU proyectado</span>
            <span>{fmtMonto(proyeccion.totalAu)}</span>
          </div>
        </div>
      </div>
    </section>
  );
};

// Bloque que se abre con un clic. `<details>` nativo: funciona con teclado y
// no necesita estado.
const Acordeon = ({ titulo, children }) => (
  <details className="au-fin-acordeon">
    <summary>{titulo}</summary>
    <div className="au-fin-acordeon-body">{children}</div>
  </details>
);

// Un pago ya hecho, cerrado. Al abrirlo muestra qué cobros lo formaron. Los
// pagos registrados antes de que el detalle trajera la tasa caen a la del
// pago completo.
const PagoRealizado = ({ pago, esAdmin }) => {
  const detalle = pago.detalle || [];
  // Respaldo si la tasa del pago no llegara (un pago viejo leído con la coma
  // del Sheet como 0): se saca del bruto sobre lo que sumaron los cobros.
  const totalDetalle = detalle.reduce((s, d) => s + Number(d.monto || 0), 0);
  const tasaPago = pago.comisionPct || (totalDetalle ? pago.bruto / totalDetalle : 0);
  const tasaDe = (d) => (typeof d.comision === 'number' ? d.comision : tasaPago);
  return (
    <details className="au-fin-acordeon au-fin-pago-realizado">
      <summary>
        {nombreQuincena(pago.corte)} · {pago.nCobros} cobro(s) · {esAdmin ? 'líquido' : 'tu líquido'}{' '}
        {fmtMonto(pago.liquido)}
        <span className="muted mono"> · pagado el {String(pago.fechaRegistro || '').split(',')[0] || '—'}</span>
      </summary>
      <div className="au-fin-acordeon-body">
        {detalle.length > 0 && (
          <div className="au-fin-table-scroll">
            <table className="au-fin-table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Cobro Flow</th>
                  <th>Mes</th>
                  <th>Comisión</th>
                  <th>Monto</th>
                  <th>Bruto</th>
                </tr>
              </thead>
              <tbody>
                {detalle.map((d, i) => (
                  <tr key={`${d.invoiceId || i}-${d.periodStart}`}>
                    <td>{d.cliente || d.email || '—'}</td>
                    <td>{fmtFecha(d.periodStart)}</td>
                    <td>{d.mes ? <span className="au-fin-tasa">Pagó mes {d.mes}</span> : <span className="muted mono">—</span>}</td>
                    <td><Comision tasa={tasaDe(d)} /></td>
                    <td>{fmtMonto(d.monto)}</td>
                    <td>{fmtMonto(Math.round(Number(d.monto || 0) * tasaDe(d)))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="au-fin-liq-breakdown">
          <div className="row"><span>Período</span><span>{pago.periodo}</span></div>
          <div className="row"><span>Bruto</span><span>{fmtMonto(pago.bruto)}</span></div>
          <div className="row"><span>Retención</span><span>{fmtMonto(pago.retencionMonto)}</span></div>
          <div className="row highlight"><span>Líquido</span><span>{fmtMonto(pago.liquido)}</span></div>
        </div>
      </div>
    </details>
  );
};

const PagoCoachCard = ({
  coach, pagos, corteVigente, payingKey, payErrors, onMarcarPagado, esAdmin,
}) => {
  const cortes = coach.cortes || [];
  // Abiertas: lo que se puede pagar y la quincena en curso. Las que vienen
  // después quedan cerradas: todavía no tienen plata y ocupaban media pantalla.
  const actuales = cortes.filter((k) => k.estado === 'pagable' || !corteVigente || k.corte <= corteVigente);
  const futuras = cortes.filter((k) => !actuales.includes(k));
  const bloque = (corte) => (
    <CorteBlock
      key={corte.corte}
      coach={coach}
      corte={corte}
      esAdmin={esAdmin}
      paying={payingKey === `${coach.email}:${corte.corte}`}
      error={payErrors[`${coach.email}:${corte.corte}`]}
      onMarcarPagado={onMarcarPagado}
    />
  );

  return (
    <div className="au-fin-card au-fin-coach-card">
      <div className="au-fin-coach-card-head">
        <div className="name">{coach.nombre}</div>
      </div>
      {coach.resumenMes && (
        <div className="au-fin-liq-breakdown au-fin-mes-resumen">
          <div className="row head">
            <span>Mes en curso · {coach.resumenMes.nCobros} cobro(s)</span>
            <span>{fmtMonto(coach.resumenMes.totalCobrado)}</span>
          </div>
          <div className="row">
            <span>{esAdmin ? `${coach.nombre} bruto` : 'Tu bruto'}</span>
            <span>{fmtMonto(coach.resumenMes.bruto)}</span>
          </div>
          <div className={`row${esAdmin ? '' : ' highlight'}`}>
            <span>{esAdmin ? `${coach.nombre} líquido` : 'Tu líquido'}</span>
            <span>{fmtMonto(coach.resumenMes.liquido)}</span>
          </div>
          {esAdmin && (
            <div className="row highlight">
              <span>
                {(() => {
                  const tasaMes = coach.resumenMes.comision === undefined
                    ? coach.comision
                    : coach.resumenMes.comision;
                  return tasaMes === null ? 'Tu parte' : `Tu parte (${fmtPct(1 - tasaMes)})`;
                })()}
              </span>
              <span>{fmtMonto(coach.resumenMes.margenAu)}</span>
            </div>
          )}
        </div>
      )}
      {cortes.length === 0 && (
        <div className="au-week-empty">&gt;&gt; Sin cobros pendientes de liquidar.</div>
      )}
      {actuales.map(bloque)}
      {futuras.length > 0 && (
        <Acordeon titulo={`Próximas quincenas (${futuras.length})`}>
          {futuras.map(bloque)}
        </Acordeon>
      )}
      {/* Dentro de la tarjeta de cada coach y no al final de la sección: ahí
          había que bajar por todas las quincenas para llegar. */}
      <Acordeon titulo={`${esAdmin ? 'Pagos realizados' : 'Pagos recibidos'} (${pagos.length})`}>
        {pagos.length === 0 ? (
          <div className="au-week-empty">&gt;&gt; Sin pagos registrados todavía.</div>
        ) : (
          // Por quincena, la más reciente arriba. El orden del ledger es el de
          // registro, y una quincena pagada tarde quedaba fuera de lugar.
          [...pagos].sort((a, b) => String(b.corte).localeCompare(String(a.corte))).map((p, i) => (
            <PagoRealizado key={`${p.corte}-${i}`} pago={p} esAdmin={esAdmin} />
          ))
        )}
      </Acordeon>
    </div>
  );
};

// `tieneClientes` distingue los dos vacíos del coach, que piden acciones
// distintas: sin clientes vinculados no hay nada que hacer todavía, pero con
// clientes y sin comisión configurada (está en adminUsuariosConfig y no en
// coachesConfig) hay algo roto que solo Agustín puede arreglar.
const PagoCoachesSection = ({
  coaches, pagos, corteVigente, payingKey, payErrors, onMarcarPagado, esAdmin, tieneClientes,
}) => (
  <section className="au-fin-section">
    <h2 className="au-fin-section-title">{esAdmin ? 'Pago a coaches' : 'Tu liquidación'}</h2>
    {coaches.length === 0 && (
      <div className="au-week-empty">
        {esAdmin && '>> No hay team coaches configurados.'}
        {!esAdmin && (tieneClientes
          ? '>> Tu comisión no está configurada todavía. Avísale a Agustín.'
          : '>> Todavía no tienes clientes vinculados en la app.')}
      </div>
    )}
    {coaches.map((coach) => (
      <PagoCoachCard
        key={coach.email}
        coach={coach}
        // Al coach el servidor ya le manda solo los suyos, y sin el correo.
        pagos={esAdmin ? pagos.filter((p) => p.coachEmail === coach.email) : pagos}
        corteVigente={corteVigente}
        esAdmin={esAdmin}
        payingKey={payingKey}
        payErrors={payErrors}
        onMarcarPagado={onMarcarPagado}
      />
    ))}
  </section>
);

// Cada coach tiene su tabla con su propio orden: por eso esto es un componente
// y no JSX suelto dentro del map. Si el estado viviera arriba, ordenar la tabla
// de un coach movería la de todos.
// Meses que lleva, comisión y lo que le deja al coach su mensualidad. Va en
// todos los clientes porque la idea es que el coach vea la antigüedad de cada
// uno y le importe cuidarla (Agustín, 2026-10-19). Sin reparto (clientes de
// Agustín, que no pagan comisión) van guiones.
const CeldasRepartoCliente = ({ reparto, monto }) => {
  if (!reparto) {
    return [0, 1, 2, 3].map((i) => <td key={i}><span className="muted mono">—</span></td>);
  }
  const n = reparto.mesesPagados || 0;
  const conMensualidad = Number(monto) > 0;
  return (
    <>
      <td>
        <span className="au-fin-tasa">
          {n === 0 ? 'Sin pagos aún' : `Lleva ${n} ${n === 1 ? 'mes pagado' : 'meses pagados'}`}
        </span>
        {reparto.mesProximo && (
          <span className="au-fin-cliente-email">
            {reparto.debeProximo ? 'Debe' : 'Ahora paga'} el mes {reparto.mesProximo}
          </span>
        )}
        {reparto.desde && (
          <span className="au-fin-cliente-email">Cliente desde {fmtFecha(reparto.desde)}</span>
        )}
      </td>
      <td><Comision tasa={reparto.comision} /></td>
      <td>{conMensualidad ? fmtMonto(reparto.bruto) : <span className="muted mono">—</span>}</td>
      <td>{conMensualidad ? fmtMonto(reparto.liquido) : <span className="muted mono">—</span>}</td>
    </>
  );
};

export const TablaClientesCoach = ({ clientes }) => {
  // Arranca ordenada por próximo pago ascendente: lo primero que se ve al
  // entrar es a quién le toca cobrar más pronto.
  const [orden, setOrden] = useState({ columna: 'proximoPago', dir: 'asc' });

  const alHacerClic = (clave) => {
    setOrden((prev) => (
      prev.columna === clave
        ? { columna: clave, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { columna: clave, dir: 'asc' }
    ));
  };

  const ordenados = ordenarClientes(clientes, orden.columna, orden.dir);

  return (
    <div className="au-fin-table-scroll">
      <table className="au-fin-table">
        <thead>
          <tr>
            {COLUMNAS_ORDENABLES.map((col) => {
              const activa = orden.columna === col.clave;
              const ariaSort = activa ? (orden.dir === 'asc' ? 'ascending' : 'descending') : 'none';
              return (
                <th key={col.clave} aria-sort={ariaSort}>
                  <button
                    type="button"
                    className={`au-fin-th-orden${activa ? ' activa' : ''}`}
                    onClick={() => alHacerClic(col.clave)}
                  >
                    {col.etiqueta}
                    <span className="au-fin-th-flecha">
                      {activa ? (orden.dir === 'asc' ? '▲' : '▼') : ''}
                    </span>
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {ordenados.map((cli) => (
            <tr key={cli.email}>
              <td>
                {cli.nombre}
                {cli.programada && (
                  <span className="au-fin-cambio-badge">
                    parte el {cli.inicio ? `${cli.inicio.slice(8, 10)}-${cli.inicio.slice(5, 7)}` : '—'}
                  </span>
                )}
                {cli.cambioPlan && (
                  <span className="au-fin-cambio-badge">
                    {cli.cambioPlan.aMonto != null ? `↑ ${fmtMonto(cli.cambioPlan.aMonto)}` : '↑ cambio'}
                    {cli.cambioPlan.fecha ? ` · ${cli.cambioPlan.fecha.slice(8, 10)}-${cli.cambioPlan.fecha.slice(5, 7)}` : ''}
                  </span>
                )}
                <span className="au-fin-cliente-email">{cli.email}</span>
              </td>
              <td>{cli.planId || '—'}</td>
              <td>{fmtMonto(cli.monto)}</td>
              <CeldasRepartoCliente reparto={cli.reparto} monto={cli.monto} />
              <td>
                {cli.estado === 'moroso' ? (
                  <span className="au-fin-status hot">Moroso · {cli.diasAtraso}d</span>
                ) : (
                  <span className="au-fin-status ok">Al día</span>
                )}
              </td>
              <td>{fmtFecha(cli.proximoPago)}</td>
              <td>{fmtFecha(cli.ultimoCobro)}</td>
              <td>{fmtFecha(cli.ultimoPago)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ClientesPorCoachSection = ({ coaches, esAdmin }) => (
  <section className="au-fin-section">
    <h2 className="au-fin-section-title">{esAdmin ? 'Clientes por coach' : 'Tus clientes'}</h2>
    {coaches.length === 0 && (
      <div className="au-week-empty">
        {esAdmin
          ? '>> Sin datos de coaches/clientes.'
          : '>> Todavía no tienes clientes vinculados en la app.'}
      </div>
    )}
    {coaches.map((coach) => (
      <div key={coach.email} className="au-fin-card au-fin-coach-card">
        <div className="au-fin-coach-card-head">
          <div className="name">{coach.nombre}</div>
          <span className="muted mono au-fin-count">{(coach.clientes || []).length} cliente(s)</span>
        </div>
        {(coach.clientes || []).length === 0 ? (
          <div className="au-week-empty">&gt;&gt; Sin clientes asignados.</div>
        ) : (
          <TablaClientesCoach clientes={coach.clientes} />
        )}
      </div>
    ))}
  </section>
);

const EstadoPendiente = ({ d }) => (
  <span className="au-fin-status hot">Moroso · {d.diasAtraso}d</span>
);

const DeudoresSection = ({ deudores, esAdmin }) => (
  <section className="au-fin-section">
    <h2 className="au-fin-section-title">Pagos pendientes</h2>
    {deudores.length === 0 ? (
      <div className="au-week-empty">
        {esAdmin ? '>> Nadie debe. ✨' : '>> Ninguno de tus clientes debe. ✨'}
      </div>
    ) : (
      <div className="au-fin-table-scroll">
        <table className="au-fin-table">
          <thead>
            <tr>
              <th>Cliente</th>
              {esAdmin && <th>Coach</th>}
              <th>Monto</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {deudores.map((d, i) => (
              <tr key={`${d.email}-${i}`}>
                <td>{d.cliente}</td>
                {esAdmin && <td>{d.coachNombre}</td>}
                <td>{fmtMonto(d.monto)}</td>
                <td><EstadoPendiente d={d} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </section>
);

// Cambios de plan que Flow tiene agendados. Se arma desde Flow y no desde la
// app, así que también muestra a quien paga sin tener cuenta: por eso la marca
// "sin cuenta en la app".
const CambiosPlanSection = ({ cambios = [] }) => {
  if (cambios.length === 0) return null;
  return (
    <section className="au-fin-section">
      <h2 className="au-fin-section-title">Cambios de plan agendados ({cambios.length})</h2>
      <div className="au-fin-card">
        <div className="au-fin-table-scroll">
          <table className="au-fin-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Hoy</th>
                <th>Pasa a</th>
                <th>Cuándo</th>
              </tr>
            </thead>
            <tbody>
              {cambios.map((c) => (
                <tr key={c.email}>
                  <td>
                    {c.nombre || c.email}
                    {c.enApp === false && <span className="muted mono"> — sin cuenta en la app</span>}
                    <span className="au-fin-cliente-email">{c.email}</span>
                  </td>
                  <td>{fmtMonto(c.montoActual)}</td>
                  <td>{c.montoNuevo != null ? fmtMonto(c.montoNuevo) : '—'}</td>
                  <td>{fmtFecha(c.fecha)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

const ReconciliacionSection = ({ reconciliacion }) => {
  if (!reconciliacion) {
    return (
      <section className="au-fin-section">
        <h2 className="au-fin-section-title">Reconciliación</h2>
        <div className="au-fin-banner warn">
          &gt;&gt; No se pudo conectar con la app — cruce por coach no disponible.
        </div>
      </section>
    );
  }

  const { enAppSinFlow = [], enFlowSinCoach = [] } = reconciliacion;

  return (
    <section className="au-fin-section">
      <h2 className="au-fin-section-title">Reconciliación</h2>

      <div className="au-fin-card">
        <div className="au-fin-coach-card-head">
          <div className="name au-fin-recon-title">En la app, sin cobro en Flow ({enAppSinFlow.length})</div>
        </div>
        {enAppSinFlow.length === 0 ? (
          <div className="au-week-empty">&gt;&gt; Sin diferencias.</div>
        ) : (
          <ul className="au-fin-list">
            {enAppSinFlow.map((c) => (
              <li key={c.email}>
                {c.nombre || c.email} <span className="muted mono">— {c.coachNombre || '—'}</span>
                <span className="au-fin-cliente-email">{c.email}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="au-fin-card">
        <div className="au-fin-coach-card-head">
          <div className="name au-fin-recon-title">En Flow, sin coach asignado ({enFlowSinCoach.length})</div>
        </div>
        {enFlowSinCoach.length === 0 ? (
          <div className="au-week-empty">&gt;&gt; Sin diferencias.</div>
        ) : (
          <ul className="au-fin-list">
            {enFlowSinCoach.map((s) => (
              <li key={s.email}>
                {s.nombre || s.email} <span className="muted mono">— {fmtMonto(s.monto)}</span>
                <span className="au-fin-cliente-email">{s.email}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

/**
 * La vista de finanzas, la misma para Agustín y para un team coach.
 *
 * Un solo componente y no dos: cuando eran dos, cada mejora al panel de
 * Agustín (tabla ordenable, badges, banners) había que reimplementarla a mano
 * en la del coach, y se quedó atrás. `esAdmin` apaga lo que es solo de él.
 *
 * Ojo: esto es presentación, NO permisos. Lo que un coach no puede ver no sale
 * del servidor (`vistaCoachFinanzas` en adminScopeCalc), y las acciones que no
 * le corresponden las corta `requireRolAdmin` en la ruta.
 */
export const FinanzasViewer = ({
  dash, pagos, reloading, onReload, onLogout,
  payingKey, payErrors, onMarcarPagado, esAdmin,
  f29Paying = false, f29Error = '', onMarcarF29 = () => {},
}) => {
  const min = minutosDesde(dash.lastUpdated);
  const coachesTeam = (dash.coaches || []).filter((c) => c.esTeamCoach);
  // Para un coach el array trae una entrada: la suya.
  const yo = esAdmin ? null : (dash.coaches || [])[0];

  return (
    <div className="au-admin au-fin-shell">
      <div className="au-fin-header">
        <div className="au-fin-header-top">
          <div className="brand">
            AU &gt;&gt; <span className="subtitle">FINANZAS</span>
          </div>
          <div className="actions">
            <button onClick={onReload} disabled={reloading}>
              {reloading ? 'ACTUALIZANDO…' : 'ACTUALIZAR'}
            </button>
            <button onClick={onLogout}>SALIR</button>
          </div>
        </div>
        <div className="au-fin-updated muted mono">
          {min === null ? '' : min === 0 ? 'Actualizado hace instantes' : `Actualizado hace ${min} min`}
          {yo && ` · ${yo.nombre} · comisión ${fmtPct(yo.comision)}`}
        </div>
      </div>

      {dash.avisoApp && (
        <div className="au-fin-banner warn">
          &gt;&gt; No se pudo conectar con au-app: {dash.avisoApp}. Los cruces de coach/clientes no están disponibles.
        </div>
      )}
      {dash.avisoLedger && (
        <div className="au-fin-banner warn">
          &gt;&gt; No se pudo leer el historial de pagos: {dash.avisoLedger}. Cortes ya pagados pueden aparecer acá como pendientes.
        </div>
      )}
      {Array.isArray(dash.invoicesSinFecha) && dash.invoicesSinFecha.length > 0 && (
        <div className="au-fin-banner warn">
          &gt;&gt; {dash.invoicesSinFecha.length} cobro(s) en Flow sin fecha de pago resuelta.
          La quincena de liquidación se calcula igual (sale de period_start), pero
          conviene mirar en Flow qué pasó con estos:
          <ul className="au-fin-list">
            {dash.invoicesSinFecha.map((i) => (
              <li key={i.invoiceId}>
                {i.cliente || i.email} · {fmtMonto(i.monto)} · período {fmtFecha(i.periodStart)}
                {' '}· factura {i.invoiceId}
              </li>
            ))}
          </ul>
        </div>
      )}

      {esAdmin && dash.avisoF29 && (
        <div className="au-fin-banner warn">
          &gt;&gt; No se pudo leer el registro del F29: {dash.avisoF29}. El recordatorio no se muestra.
        </div>
      )}
      {esAdmin && (
        <F29Section f29={dash.f29} paying={f29Paying} error={f29Error} onMarcar={onMarcarF29} />
      )}

      <ResumenSection resumen={dash.resumen} corte={dash?.ventana?.corte} />

      <ProyeccionSection proyeccion={dash.proyeccion} esAdmin={esAdmin} />

      <PagoCoachesSection
        coaches={coachesTeam}
        pagos={pagos}
        corteVigente={dash?.ventana?.corte}
        payingKey={payingKey}
        payErrors={payErrors}
        onMarcarPagado={onMarcarPagado}
        esAdmin={esAdmin}
        tieneClientes={(yo?.clientes || []).length > 0}
      />

      <ClientesPorCoachSection coaches={dash.coaches || []} esAdmin={esAdmin} />

      <DeudoresSection deudores={dash.deudores || []} esAdmin={esAdmin} />

      <CambiosPlanSection cambios={dash.cambiosPlan} />

      {/* Solo para Agustín, y no por pudor: al coach no le llega
          `reconciliacion`, y esa sección lee la ausencia del campo como "no se
          pudo conectar con la app". Renderizarla le mostraría una falla que no
          ocurrió. Además su mitad útil (clientes sin coach asignado) es global
          por definición y no hay versión suya que tenga sentido. */}
      {esAdmin && <ReconciliacionSection reconciliacion={dash.reconciliacion} />}
    </div>
  );
};

// ---------- Página principal ----------

const AdminFinanzasPage = () => {
  const [stage, setStage] = useState('checking'); // 'checking' | 'login' | 'viewer' | 'error'
  const [dash, setDash] = useState(null);
  const [pagos, setPagos] = useState([]);
  const [reloading, setReloading] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [payingKey, setPayingKey] = useState(null); // `${coachEmail}:${corte}`
  const [payErrors, setPayErrors] = useState({});
  const [f29Paying, setF29Paying] = useState(false);
  const [f29Error, setF29Error] = useState('');
  const [sesion, setSesion] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [dashRes, ses] = await Promise.all([
        axios.get(`${ADMIN_API}/api/admin/finanzas/dashboard`, { withCredentials: true }),
        getSesion(),
      ]);
      setDash(dashRes.data);
      setSesion(ses);
      setStage('viewer');
      try {
        const pagosRes = await axios.get(`${ADMIN_API}/api/admin/finanzas/pagos`, {
          withCredentials: true,
        });
        setPagos(pagosRes.data?.pagos || []);
      } catch (e) {
        // Historial no crítico: si falla, seguimos mostrando el dashboard igual.
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        setStage('login');
      } else if (err?.response?.status === 403 && await llevarSetterASetting()) {
        // Un setter no tiene nada acá: se fue a /admin/setting.
      } else {
        setErrMsg(err?.response?.data?.error || 'No se pudo conectar con el servidor.');
        setStage('error');
      }
    }
  }, []);

  const reload = async () => {
    setReloading(true);
    try {
      // Un coach solo puede refrescar SU vista, así que reusa el caché en vez
      // de invalidarlo: `refresh` recorre toda la API de Flow para todos los
      // coaches, y el botón más grande de su pantalla no es lugar para eso.
      // Los datos de un coach cambian una vez al mes por cliente (cuando Flow
      // cobra), no hay ninguna razón para pagar ese costo en cada clic.
      const res = sesion?.rol === 'admin'
        ? await axios.post(`${ADMIN_API}/api/admin/finanzas/refresh`, {}, { withCredentials: true })
        : await axios.get(`${ADMIN_API}/api/admin/finanzas/dashboard`, { withCredentials: true });
      setDash(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        setStage('login');
      } else {
        // eslint-disable-next-line no-alert
        alert(err?.response?.data?.error || 'No se pudo actualizar. Intenta de nuevo.');
      }
    } finally {
      setReloading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${ADMIN_API}/api/admin/logout`, {}, { withCredentials: true });
    } catch (err) {
      // Ignorar error de logout — igual mostramos login
    }
    setDash(null);
    setStage('login');
  };

  const marcarComoPagado = async (coach, corte) => {
    if (!corte) return;
    const clave = `${coach.email}:${corte.corte}`;
    // eslint-disable-next-line no-alert
    const confirmado = window.confirm(mensajeConfirmacionPago(coach, corte));
    if (!confirmado) return;

    setPayErrors((prev) => ({ ...prev, [clave]: '' }));
    setPayingKey(clave);
    try {
      await axios.post(
        `${ADMIN_API}/api/admin/finanzas/pagos`,
        { coachEmail: coach.email, corte: corte.corte },
        { withCredentials: true },
      );
      await fetchAll();
    } catch (err) {
      if (err?.response?.status === 401) {
        setStage('login');
        return;
      }
      const msg = err?.response?.data?.error || 'No se pudo registrar el pago.';
      setPayErrors((prev) => ({ ...prev, [clave]: msg }));
    } finally {
      setPayingKey(null);
    }
  };

  const marcarF29 = async (periodo) => {
    // eslint-disable-next-line no-alert
    const confirmado = window.confirm(
      `¿Marcar como pagado el F29 de ${nombreMes(periodo.periodo)}?\n\n`
      + 'Hazlo después de pagarlo en sii.cl: esto solo apaga el recordatorio.',
    );
    if (!confirmado) return;
    setF29Error('');
    setF29Paying(true);
    try {
      await axios.post(
        `${ADMIN_API}/api/admin/finanzas/f29`,
        { periodo: periodo.periodo },
        { withCredentials: true },
      );
      await fetchAll();
    } catch (err) {
      if (err?.response?.status === 401) {
        setStage('login');
        return;
      }
      setF29Error(err?.response?.data?.error || 'No se pudo marcar el F29.');
    } finally {
      setF29Paying(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  if (stage === 'checking') {
    return (
      <div className="au-admin au-fin-shell">
        <div className="au-week-empty">&gt;&gt; Conectando…</div>
      </div>
    );
  }

  if (stage === 'error') {
    return (
      <div className="au-admin au-fin-shell">
        <div className="au-week-empty">
          &gt;&gt; {errMsg}{' '}
          <button
            className="au-fin-retry-btn"
            onClick={() => {
              setStage('checking');
              fetchAll();
            }}
          >
            REINTENTAR
          </button>
        </div>
      </div>
    );
  }

  if (stage === 'login') {
    return <AdminLogin onSuccess={(datos) => { if (setterAlEntrar(datos)) return; setStage('checking'); fetchAll(); }} />;
  }

  return (
    <>
      <div className="au-admin au-admin-nav-wrap">
        <AdminNav actual="finanzas" rol={sesion?.rol} />
      </div>
      <FinanzasViewer
        dash={dash}
        // Sin sesión resuelta se asume coach: si algo fallara al leer el rol,
        // el error es mostrar de menos y no de más.
        esAdmin={sesion?.rol === 'admin'}
        pagos={pagos}
        reloading={reloading}
        onReload={reload}
        onLogout={logout}
        payingKey={payingKey}
        payErrors={payErrors}
        onMarcarPagado={marcarComoPagado}
        f29Paying={f29Paying}
        f29Error={f29Error}
        onMarcarF29={marcarF29}
      />
    </>
  );
};

export default AdminFinanzasPage;
